/* ***************************************************************** */
/*                                                                   */
/* Licensed Materials - Property of Merative                         */
/*                                                                   */
/* (C) Copyright Merative Corp. 2025                                 */
/*                                                                   */
/* ***************************************************************** */

import React, { Fragment, useEffect } from 'react';
import {
    useDispatch,
    useSelector
} from "react-redux";
import {
    fetchAndSaveMonograph,
    saveCitationId,
    selectCitationId,
    selectNeopedMonograph
} from "./redux/neoped_monograph_redux";
import { useHistory } from "react-router-dom";
import {
    get,
    getQueryParameters
} from "../../../../../utils";
import {
    Select,
    SelectItem,
    SelectItemGroup
} from "@carbon/react";
import { getNeoPedMonographPromise } from "../../../../../services/neofaxPediatricsService";
import { CitationNeoPedModalPopup } from "../../../../../components/schema-based-renderer/CitationModalPopup";
import {
    ACTIVE_STATUS,
    NONE_STATUS
} from "../../../../../components/response-loading/redux/response-loading-redux";
import {
    TITLE_DRUG_MONOGRAPHS_RESULTS_PREFIX
} from "../../../../../components/common/constants";
import {
    NEO_PED_DRUG_ID_KEY,
    NEO_PED_MONOGRAPH_DEFAULT_SECTION,
    NEONATAL,
    RESULT_TYPE_NEOFAX,
    RESULT_TYPE_PEDIATRICS, RESULT_TYPE_PEDIATRICS_UPPER_CASE
} from "../../../common/constants";

export const NeoPedMonographComponent = (props) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const login_page = useSelector((state) => state.login_page);
    const citationId = useSelector(selectCitationId);

    useEffect(() => {
        if (performance.getEntriesByType("navigation")[0]?.type === 'reload') {
            dispatch(saveCitationId(""));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pathname = get(props, "location.pathname", "");
    const loc_search = get(props, "location.search", {});
    const searchParams = new URLSearchParams(loc_search);
    const documentId = searchParams.get("documentId");
    const drugFromSearch = decodeURIComponent(searchParams.get("drug"));
    const topic_id = searchParams.get("topicId") || NEO_PED_MONOGRAPH_DEFAULT_SECTION;
    const drugType = props.isNeonatal ? RESULT_TYPE_NEOFAX : RESULT_TYPE_PEDIATRICS;
    const all_neoped_monograph_state = useSelector(selectNeopedMonograph);
    const neoped_state = get(all_neoped_monograph_state, drugType, {});
    const neoped_monograph = get(neoped_state, "monograph", {});
    const status = get(neoped_state, "status", NONE_STATUS);
    const neoped_monograph_state = NONE_STATUS === status ? get(neoped_monograph, "docs[0]", {}) : {};
    const topics = neoped_monograph_state?.topics || [];
    const title = get(neoped_monograph_state, "title", drugFromSearch);

    const isTopicValid = topics.some(topic =>
        topic.subtopics.some(subtopic => subtopic.sectionKey === topic_id)
    );
    const topicName = isTopicValid ? topic_id : NEO_PED_MONOGRAPH_DEFAULT_SECTION;

    const topicComponent = topics.map(topic =>
        <SelectItemGroup key={topic.name} label={topic.name}>
            {topic.subtopics.map(subtopic => (
                <SelectItem key={subtopic.sectionKey} value={subtopic.sectionKey} text={subtopic.name} />
            ))}
        </SelectItemGroup>
    );

    window.navigateToPosition = (citationId) => {
        dispatch(saveCitationId(citationId));
    }

    const populateDocumentTitle = () => {
        const drugTypeFromSearch = new URLSearchParams(history.location.search).get("tab") === RESULT_TYPE_NEOFAX
          ? NEONATAL
          : RESULT_TYPE_PEDIATRICS_UPPER_CASE;
        const matchedTopic = topics.find(topic => topic.subtopics.some(subtopic => subtopic.sectionKey === topic_id));
        const currentTopic = matchedTopic?.name || NEO_PED_MONOGRAPH_DEFAULT_SECTION;
        const currentSubtopic = matchedTopic?.subtopics.find(subtopic => subtopic.sectionKey === topic_id)?.name;
        const titleParts = [ TITLE_DRUG_MONOGRAPHS_RESULTS_PREFIX, drugTypeFromSearch, currentTopic ];
        if (currentSubtopic) {
            titleParts.push(currentSubtopic);
        }
        document.title = titleParts.join(' - ');
    }

    useEffect(() => {
        populateDocumentTitle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, topics, topic_id, history]);

    const content = get(neoped_monograph_state,"content", []);
    let contentArray = [];
    if (topicName === NEO_PED_MONOGRAPH_DEFAULT_SECTION || topicName === "undefined" || topicName === "null") {
        topics.forEach(topic => {
            contentArray.push(<h3 className='monograph-topic'>{topic.name} </h3>);
            topic.subtopics.forEach(subtopic =>
            {
                contentArray.push(<h4 className='monograph-subtopic'>{subtopic.name} </h4>);
                content.forEach(item => {
                    if (item.sectionKey === subtopic.sectionKey) {
                        contentArray.push(<div className='mdx-grouped-parent-div' dangerouslySetInnerHTML={{ __html:item.value  }} />);
                    }
                });
            })
        });
    } else {
        for (let index = 0; index < content.length; index++) {
            const item = content[index];
            if (item.sectionKey === topicName) {
                topics.find(t => {
                    t.subtopics.some(subtopic => {
                        if (subtopic.sectionKey === topicName) {
                            contentArray.push(<h3 className='monograph-topic'>{t.name}</h3>);
                            contentArray.push(<h4 className='monograph-subtopic'>{subtopic.name} </h4>);
                            return true;
                        }
                        return false;
                    });
                    return false;
                });
                contentArray.push(<div className='mdx-grouped-parent-div' dangerouslySetInnerHTML={{ __html:item.value  }} />)
            }
        }
    }
    const referenceSectionContent = content.find(item => item.sectionKey === "referenceSection");
    const referenceSectionHTMLElement = document.createElement('div');
    const citationContent = document.createElement("div");
    let citationModalPopup;
    if (citationId && referenceSectionContent) {
        referenceSectionHTMLElement.innerHTML = referenceSectionContent.value;
        const citationDiv = referenceSectionHTMLElement.lastElementChild;
        const citationNodes = citationDiv.childNodes;
        let isIdFound = false;
        for (const child of citationNodes) {
            const clonedChild = child.cloneNode(true);
            if (isIdFound) {
                if (clonedChild.id) {
                    break;
                }
                citationContent.appendChild(clonedChild);
            }
            else if (clonedChild.id === citationId) {
                isIdFound = true;
            }
        }
        citationModalPopup = <CitationNeoPedModalPopup
            key="citation-popup"
            content={citationContent}
            onCloseHandler={() => window.navigateToPosition('')}
        />
    } else {
        citationModalPopup = <Fragment key="citation-popup"/>
    }

    const menuSelectionHandler = (event) => {
        if (sessionStorage.getItem(NEO_PED_DRUG_ID_KEY)) {
            sessionStorage.removeItem(NEO_PED_DRUG_ID_KEY);
        }
        const select_element = event.target;
        const updatedTopicName = select_element.options[select_element.selectedIndex].value
        if (topicName !== updatedTopicName) {
            const newQueryParameters = getQueryParameters(loc_search, {topicId: updatedTopicName});
            history.push({
                pathname: pathname,
                search: newQueryParameters
            });
          
            const scrollArea = document.querySelector('.mdx-scroll-area');
            if (scrollArea) {
                scrollArea.scrollTop = 0;
            }
        }
    }

    useEffect(() => {
        if (ACTIVE_STATUS !== status) {
            dispatch(fetchAndSaveMonograph({
                fetcher: () => getNeoPedMonographPromise({ result_type: drugType, documentId: documentId, login_page }),
                drugType: drugType,
                documentId: documentId,
                history,
                loginPage: login_page
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drugFromSearch]);

    return (<div className="neoped-monograph-page" data-testid="neoped-monograph-page">
                <div className="mdx-header grey-banner">
                    <h2>{title}</h2>
                    {drugFromSearch !== title && <p className='brand-name-monograph'>[Your search: {drugFromSearch}]</p>}
                    <h5>Section</h5>
                    <Select
                        {...props}
                        value={topicName}
                        id="neopedMenu"
                        labelText=""
                        onChange={(event) => menuSelectionHandler(event)}
                        className="neopedSelect"
                    >
                        <SelectItem value={NEO_PED_MONOGRAPH_DEFAULT_SECTION} text="View Full Document"/>)
                        {topicComponent}
                    </Select>
                </div>
                {contentArray}
                {citationModalPopup}
            </div>
    );
};
